/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The plan step: pick a segment, pick a billing cycle, pick a tier.
 *
 * The step used to sit third in the wizard, after the customer had already
 * said whether they were a broker or an agency and filled in a form. Both of
 * those are gone: the segment is chosen here, on the segmented control above
 * the cards, and the data the subscription needs is collected in the checkout.
 * Choosing a tier is therefore the first thing that happens, and it commits —
 * the card is the button, and it goes straight to the checkout.
 *
 * Nothing on this screen is written down in code: prices, quotas, features and
 * the seat range all come from the published catalogue.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreSetters } from '@/shared/state';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { Text, useStepsContext } from '@/shared/ui';
import type { PlanSegment } from '@/features/pricing';
import { usePlans } from '../../hooks/use-plans';
import type { BillingCycle, PlanWithFrequency } from '../../hooks/use-plans';
import { onboardingFormContext } from '../../context/OnboardingContext';
import { PlanCard, PlanChoiceSkeleton } from './PlanChoice.parts';
import { buildQuotaLabels, formatStorage, storageNote } from './PlanChoice.catalog';
import { usePlanWaitlistFlow } from './usePlanWaitlistFlow';
import { AgencyChoice } from './PlanChoice.agencyCards';
import { PlanCarousel } from './PlanChoice.carouselTrack';
import { TrialEligibilityNotice } from './PlanChoice.trialNotice';
import { useTrialEligibility } from '../../hooks/use-trial-eligibility';

/** How much of the matrix a phone shows before the rest is folded away. */
const MOBILE_FEATURE_ROWS = 5;
import { SegmentToggle } from './PlanChoice.chooser';

/** Months a yearly subscription is billed for. Two of the twelve are free. */
const MONTHS_PER_YEAR = 12;

/** Where "Fale com vendas" writes to. */
const SALES_EMAIL = 'comercial@housien.com.br';

type PlanSelectionValues = {
  selectedPlanId: string;
  selectedPlanName: string;
  selectedBillingFrequency: string;
  selectedPlanKey: string;
  selectedPlanPrice: number;
  selectedSegment: PlanSegment;
  agencyType: 'solo_broker' | 'agency';
};

/**
 * Resolves the concrete plan id for the selected billing cycle.
 *
 * @param plan - The plan with monthly/yearly variants.
 * @param isYearly - Whether the yearly cycle is selected.
 * @returns The resolved plan id.
 */
function resolvePlanId(plan: PlanWithFrequency, isYearly: boolean): string {
  const id = isYearly ? plan.yearlyId : plan.monthlyId;
  return id || plan.originalId || plan.key;
}

/**
 * The plan's price for one month, whichever cycle is showing.
 *
 * The yearly plan is priced for the whole year, so the comparable number is a
 * twelfth of it — not the yearly total, which would read as a tenfold price
 * rise beside the monthly card.
 *
 * @param plan - The selected plan.
 * @param isYearly - Whether the yearly cycle is selected.
 * @returns The monthly-equivalent price of one seat.
 */
function resolveMonthly(plan: PlanWithFrequency, isYearly: boolean): number {
  const total = (isYearly ? plan.yearlyPrice : plan.monthlyPrice) || 0;
  return isYearly ? total / MONTHS_PER_YEAR : total;
}

/**
 * Builds the plan-selection values persisted to the onboarding store.
 *
 * The segment travels with the selection because the checkout needs it for
 * more than pricing: it decides whether a seat count is asked for at all, and
 * whether the document field wants a CPF or a CNPJ.
 *
 * @param plan - The selected plan.
 * @param billingCycle - The chosen billing cycle.
 * @param segment - The chosen segment.
 * @returns The plan-selection values.
 */
function buildPlanSelection(
  plan: PlanWithFrequency, billingCycle: BillingCycle, segment: PlanSegment,
): PlanSelectionValues {
  const isYearly = billingCycle === 'yearly';
  return {
    selectedPlanId: resolvePlanId(plan, isYearly),
    selectedPlanName: plan.title,
    selectedBillingFrequency: billingCycle,
    selectedPlanKey: plan.key,
    selectedPlanPrice: resolveMonthly(plan, isYearly),
    selectedSegment: segment,
    agencyType: segment === 'solo' ? 'solo_broker' : 'agency',
  };
}

/**
 * Storage, restated in one sentence under the grid.
 *
 * It is also a row inside every card, which is where the comparison lives.
 * The sentence is the part someone reads when they are not comparing — "how
 * much do I get?" — and reading it off three cards is worse than reading it
 * off one line.
 *
 * @param props - The per-plan storage entries.
 * @returns The footnote, or null when the catalogue reports no storage.
 */
const StorageNote: React.FC<{ entries: ReturnType<typeof storageNote> }> = ({ entries }) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  if (entries.length === 0) return null;
  const number = (n: number) => new Intl.NumberFormat(i18n.language).format(n);
  // ListFormat, not a joined string: "1 GB no Esmeralda, 100 GB no Rubi e 1 TB
  // no Diamante" needs the conjunction before the last item, and which word
  // that is — and whether it takes a comma — is the locale's business.
  const list = new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(
    entries.map((e) => t('planChoice.storageEntry', {
      plan: e.title, amount: formatStorage(e.gigabytes, number),
    })),
  );
  return (
    <Text
      component="p"
      style={{
        margin: 0, fontSize: 'var(--text-tag)',
        color: 'var(--text-tertiary)', textAlign: 'center',
      }}
    >
      {t('planChoice.storageNote', { list })}
    </Text>
  );
};

type PlanChoiceProps = {
  /**
   * Restricts the screen to one segment, hiding the chooser.
   *
   * There is no moving between Imobiliária and Corretor solo once an account
   * exists, so every plan screen reached from one (activating a draft,
   * reactivating, changing plan) fixes the segment to the account's own. The
   * chooser only ever belongs to a brand new account.
   */
  lockedSegment?: PlanSegment;
  /**
   * The agency being subscribed, when it exists: the free period is the
   * owner's, so it is asked about them. Omitted during onboarding, where the
   * signed-in user is the owner.
   */
  agencyId?: string;
};

const PlanChoice: React.FC<PlanChoiceProps> = ({ lockedSegment, agencyId }) => {
  const { setCurrentStepId } = useStepsContext();
  const { updateValues } = useStoreSetters();
  const { useStore } = onboardingFormContext;
  const storedType = useStore((s: any) => s.values?.agencyType) as string | undefined;
  const { t } = useTranslation('@pages/onboarding');
  // 768: below this the three cards cannot sit side by side, which is the
  // only reason they are cards.
  const isPhone = useMediaQuery('(max-width: 768px)');
  // One state for all three cards: the buyer opened the list to compare, and
  // three cards of different heights is not a comparison.
  const [allFeatures, setAllFeatures] = useState(false);

  const [segment, setSegmentChoice] = useState<PlanSegment>(
    lockedSegment ?? (storedType === 'solo_broker' ? 'solo' : 'agency'),
  );
  const {
    isLoading, plans, billingCycle, setBillingCycle, setSegment, availableSegments,
  } = usePlans({ defaultSegment: segment });

  // Read off the catalogue, never derived here. The yearly price is already
  // ten months of the monthly one, so a screen doing its own arithmetic would
  // produce 16.67 and have to decide how to round it — a second opinion about
  // a number the catalogue already states, which is how the badge and the
  // footer came to describe the same offer two different ways.
  const yearlyDiscountPercent = plans.find(
    (plan) => (plan.discountPercent ?? 0) > 0,
  )?.discountPercent;

  // The quota ladder is a *comparison*, so it is built once across the whole
  // catalogue rather than per card: "4x mais" means nothing until you know
  // what the entry plan carries.
  const quotaLabels = useMemo(() => buildQuotaLabels(plans), [plans]);
  const storage = useMemo(() => storageNote(plans), [plans]);
  const waitlist = usePlanWaitlistFlow(plans);
  // The badge is a promise the charge has to keep, so it waits for the answer.
  const trialStatus = useTrialEligibility(agencyId);
  const trialOffered = trialStatus === 'eligible';

  const handleSegment = (next: PlanSegment) => {
    setSegmentChoice(next);
    setSegment(next);
  };

  // The account's segment is read from the backend, so it lands after the
  // first render: using it only as the initial state left the screen showing
  // the agency tiers to a solo broker, which is exactly what locking the
  // segment was for.
  useEffect(() => {
    if (!lockedSegment || lockedSegment === segment) return;
    handleSegment(lockedSegment);
  }, [lockedSegment, segment]);

  // The stored account type can point at a segment that has nothing on sale —
  // a returning solo broker when only the agency tiers opened, or the reverse.
  // Without this the chooser hides and the step strands them on three cards
  // they cannot buy, which is the dead end hiding the chooser meant to avoid.
  useEffect(() => {
    if (lockedSegment) return;
    if (!availableSegments.length || availableSegments.includes(segment)) return;
    handleSegment(availableSegments[0]);
  }, [availableSegments, segment, lockedSegment]);

  // The cycle is chosen again, per card, at checkout — this screen only ever
  // shows the yearly price, so there is nothing here for a toggle to switch.
  useEffect(() => {
    setBillingCycle('yearly');
  }, []);

  // Choosing a tier is the commitment: there is no Continue button under the
  // grid, because a card that looks like a button and then needs a second
  // click somewhere else is the slowest way through this screen.
  const handleSelectPlan = (plan: PlanWithFrequency) => {
    updateValues(buildPlanSelection(plan, billingCycle, segment));
    setCurrentStepId('checkout');
  };

  return (
    <main
      style={{
        width: '100%', maxWidth: 1160, margin: '0 auto',
        padding: isPhone
          ? '0 0 var(--space-8)'
          : 'var(--space-2) var(--space-6) var(--space-12)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: isPhone ? 'var(--space-2)' : 'var(--space-3)',
        /*
         * `clip`, not `hidden`: a box that hides one axis and leaves the
         * other visible computes the other to `auto`, which turned this into
         * a scroll container of its own and drew a scrollbar down the middle
         * of the page. `clip` cuts the overflow without the side effect.
         */
        overflowX: 'clip',
        flexShrink: 0,
      }}
    >
      <header
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 'var(--space-1)', textAlign: 'center',
          padding: isPhone ? '0 var(--space-4)' : 0,
        }}
      >
        <Text
          component="h1"
          style={{
            margin: 0, fontFamily: 'var(--font-family-alt-2)',
            fontSize: isPhone ? '1.875rem' : 'clamp(2rem, 5vw, 2.75rem)', lineHeight: 1.15,
            fontWeight: 'var(--font-bold)', letterSpacing: '-0.025em',
            color: 'var(--text-primary)',
          }}
        >
          {t('planChoice.title')}
        </Text>
        <Text
          component="p"
          style={{
            margin: 0, fontSize: isPhone ? 'var(--text-body)' : 'var(--text-app-heading)', lineHeight: 1.5,
            color: 'var(--text-secondary)', maxWidth: isPhone ? '100%' : '54ch', textWrap: 'pretty',
          }}
        >
          {lockedSegment
            ? t(`planChoice.subtitleLocked.${lockedSegment}`)
            : t('planChoice.subtitle')}
        </Text>
      </header>

      <div style={{ padding: isPhone ? '0 var(--space-4)' : 0 }}>
        <SegmentToggle
          value={segment}
          onChange={handleSegment}
          segments={lockedSegment ? [lockedSegment] : availableSegments}
        />
      </div>

      <div style={{ padding: isPhone ? '0 var(--space-4)' : 0, textAlign: 'center' }}>
        <TrialEligibilityNotice status={trialStatus} segment={segment} />
      </div>

      {(() => {
        if (isLoading) return <PlanChoiceSkeleton />;
        // An agency buys a team, not a tier: the tiers are rows inside one
        // card, because the seat counts are chosen at checkout and may mix.
        // A solo broker does buy a tier, so there the cards stay.
        if (segment === 'agency') {
          return (
            <AgencyChoice
              plans={plans}
              billingCycle={billingCycle}
              quotaLabels={quotaLabels}
              onSelect={handleSelectPlan}
              salesHref={`mailto:${SALES_EMAIL}`}
              trialOffered={trialOffered}
            />
          );
        }
        // One card per screen on a phone, side by side above it. Stacked,
        // the three cards are a scroll the buyer has to remember across --
        // and the whole point of the tiers is reading them against each
        // other. The carousel keeps the next one half in view.
        const cards = plans.map((plan) => (
          <PlanCard
            key={plan.key}
            plan={plan}
            billingCycle={billingCycle}
            selected={false}
            onSelect={handleSelectPlan}
            onSoldOutAction={waitlist.joinSoldOut}
            isWaitlisted={waitlist.isWaitlisted(plan.key)}
            ctaLabel={t('planChoice.subscribeTo', { plan: plan.title })}
            quotaLabels={quotaLabels[plan.key]}
            fillHeight
            featureLimit={isPhone ? MOBILE_FEATURE_ROWS : undefined}
            featuresExpanded={allFeatures}
            trialOffered={trialOffered}
            onToggleFeatures={isPhone ? () => setAllFeatures((open) => !open) : undefined}
          />
        ));
        if (isPhone) return <PlanCarousel>{cards}</PlanCarousel>;
        return (
          <div
            style={{
              width: '100%', display: 'grid',
              // auto-fit rather than three fixed columns: at 1280 the three
              // cards sit side by side, and below ~900 they reflow to two and
              // then one without a breakpoint of their own.
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--space-4)', alignItems: 'stretch',
            }}
          >
            {cards}
          </div>
        );
      })()}

      {segment === 'agency' ? null : (
        <div style={{ padding: isPhone ? '0 var(--space-4)' : 0, textAlign: 'center' }}>
          <StorageNote entries={storage} />
        </div>
      )}

      <Text
        component="p"
        style={{
          margin: 0, fontSize: 'var(--text-tag)',
          color: 'var(--text-tertiary)', textAlign: 'center',
          padding: isPhone ? '0 var(--space-4)' : 0,
        }}
      >
        {t('planChoice.footer', { percent: yearlyDiscountPercent ?? '' })}
      </Text>
    </main>
  );
};

export default PlanChoice;
